// These transformers will run for all macros
import type { FieldTransformer } from '../fieldTypes.ts';

import { cql } from './cql.ts';
import { parseBoolean } from './parse-boolean.ts';
import { stripDefaultValues } from './strip-default-values.ts';
import { transformEmptyKey } from './transform-empty-key.ts';
import { transformMultiValuedFields } from './transform-multi-valued-fields.ts';
// eslint-disable-next-line import/no-cycle
import { missingRequiredParameters } from './missing-required-parameters.ts';

export const composeTransformers = <T extends {}>(
	...transformers: FieldTransformer<T extends infer R ? R : T>[]
) => ({
	transformFields: (fields: any, pageContext: any, intl: any): Promise<any> =>
		transformers.reduce(async (transformedFields, transformer) => {
			// We await transformedFields accumulator because transformFields returns a Promise<FieldDefinition[] | DynamicFieldDefinitions>
			const fieldsResult = await transformedFields;
			return transformer.transformFields
				? transformer.transformFields({ fields: fieldsResult, pageContext, intl }) // Call transformFields on fields if it exists
				: transformedFields; // Otherwise, return the original fields with any applied transformations thus far
		}, Promise.resolve(fields)),
	transformBefore: (params: any, metadata: any, pageContext: any): any =>
		transformers.reduce(
			(params, transformer) =>
				transformer.transformBefore
					? transformer.transformBefore(params, metadata, pageContext)
					: params,
			params,
		),
	transformAfter: (params: any, metadata: any, pageContext: any): any =>
		transformers.reduce(
			(params, transformer) =>
				transformer.transformAfter
					? transformer.transformAfter(params, metadata, pageContext)
					: params,
			params,
		),
});

/**
 * IMPORTANT: ORDER MATTERS HERE, Please don't sort it for style purpose
 */
export const always: {
	transformFields: (fields: any, pageContext: any, intl: any) => Promise<any>;
	transformBefore: (params: any, metadata: any, pageContext: any) => any;
	transformAfter: (params: any, metadata: any, pageContext: any) => any;
} = composeTransformers(
	cql,
	transformEmptyKey,
	parseBoolean,
	transformMultiValuedFields,
	stripDefaultValues,
	missingRequiredParameters,
);
